/**
 * SidebarNavItem — the ONE nav-item shape used by every sidebar row.
 *
 * Handles both modes:
 *   - Expanded  → icon + label + optional right-side badge (count / dot)
 *   - Collapsed → icon-only, centered, `title` attribute used as tooltip
 *
 * Active state uses the DESIGN.md pattern: 2px accent slab on the left edge
 * (an inset absolute element, NOT a border-left), plus a subtle ink tint on
 * the label. Every sidebar row in the module renders through this primitive
 * so no author can invent their own row shape.
 */
import type { ComponentType, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { cn } from '../../lib/cn';

type IconComponent = ComponentType<{ className?: string | undefined }>;

export interface SidebarNavItemProps {
  to: string;
  label: string;
  icon: IconComponent;
  collapsed?: boolean;
  /** Right-side accessory (e.g. a count chip). Only shown when expanded. */
  badge?: ReactNode;
  /** For agent-style rows: a small colored square with initials instead of the lucide icon. */
  avatar?: {
    text: string;
    color?: string | undefined;
  };
  /** Optional class override for the icon color (e.g. per-sheet accent). */
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
}

export function SidebarNavItem({
  to,
  label,
  icon: Icon,
  collapsed = false,
  badge,
  avatar,
  iconClassName,
  iconStyle,
}: SidebarNavItemProps): JSX.Element {
  if (collapsed) {
    return (
      <NavLink
        to={to}
        title={label}
        aria-label={label}
        className={({ isActive }) =>
          cn(
            'group relative mx-auto my-1 grid size-8 place-items-center rounded-tight text-ink-muted ' +
              'transition-colors duration-instant ease-standard hover:text-ink',
            isActive && 'bg-paper-sunk text-ink',
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive ? (
              <span
                aria-hidden
                className="absolute -left-2 top-1.5 h-[calc(100%-12px)] w-0.5 rounded-pill bg-accent"
              />
            ) : null}
            {avatar ? (
              <span
                className="grid size-6 place-items-center rounded-tight text-[10px] font-bold text-paper"
                style={avatar.color ? { background: avatar.color } : undefined}
              >
                {avatar.text}
              </span>
            ) : (
              <Icon
                className={cn('size-4', iconClassName)}
                {...(iconStyle ? { style: iconStyle } : {})}
              />
            )}
          </>
        )}
      </NavLink>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-tight px-2.5 py-1.5 text-sm text-ink-soft ' +
            'transition-colors duration-instant ease-standard hover:bg-paper-sunk hover:text-ink',
          isActive && 'bg-paper-sunk font-medium text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? (
            <span
              aria-hidden
              className="absolute left-0 top-1.5 h-[calc(100%-12px)] w-0.5 rounded-pill bg-accent"
            />
          ) : null}
          {avatar ? (
            <span
              className="grid size-6 shrink-0 place-items-center rounded-tight text-[10px] font-bold text-paper"
              style={avatar.color ? { background: avatar.color } : undefined}
            >
              {avatar.text}
            </span>
          ) : (
            <span className={cn('grid size-6 shrink-0 place-items-center text-ink-muted', iconClassName)}>
              <Icon
                className="size-4"
                {...(iconStyle ? { style: iconStyle } : {})}
              />
            </span>
          )}
          <span className="truncate">{label}</span>
          {badge ? <span className="ml-auto shrink-0">{badge}</span> : null}
        </>
      )}
    </NavLink>
  );
}
